import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Typography,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import { Grid } from "@material-ui/core";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { materialsymbolsupload, materialsymbolsdownload, Delete } from "images";
import { alertActions } from "_store";
import { fileExtension, fileSizeReadable, fileTypeAcceptable } from "_utils";
import { saveAs } from "file-saver";

/**
 * Filter for the Base and Heat Factors Report.
 * Mirrors the pattern used in ReportFilter.jsx (account balance report):
 * a single search field plus a mass-search file upload (.txt only).
 */
const BaseAndHeatFactorReportFilter = ({
  accountNumber,
  setAccountNumber,
  onSearchSubmit,
  onClear,
  header = "Base and Heat Factors Report",
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [fileName, setFileName] = useState("");

  const id = isOpen && Boolean(anchorEl) ? "simple-popper" : undefined;

  const supportedFormats = [".txt"];
  const massSearchFormats = [".txt"];
  const MAX_FILE_SIZE_MB = 5;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleError = (error) => {
    dispatch(
      alertActions.error({
        message: error.message,
        header,
      })
    );
    setIsOpen(false);
  };

  const handleFileUpload = async (event) => {
    dispatch(alertActions.clear());
    event.preventDefault();

    const filesAdded = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files;

    if (!filesAdded || filesAdded.length === 0) return;

    const file = filesAdded[0];

    file.extension = fileExtension(file);
    file.sizeReadable = fileSizeReadable(file.size);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      handleError({
        code: "ERR_MSG_050",
        message: `File size cannot exceed ${MAX_FILE_SIZE_MB} MB `,
      });
      return;
    }

    if (!fileTypeAcceptable(massSearchFormats, file)) {
      handleError({
        code: "ERR_MSG_084",
        message: "Invalid file format. Only text file is supported. ",
      });
      return;
    }

    if (file.size === 0) {
      handleError({
        code: "ERR_MSG_086",
        message: "File is empty ",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;

        const numbers = content
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        setUploadedFile(file);
        setAccountNumbers(numbers);
        setFileName(file.name);
        setAccountNumber("");
      } catch (error) {
        handleError({
          code: "ERR_FILE_READ",
          message: "Error reading file content",
        });
      }
    };

    reader.onerror = () => {
      handleError({
        code: "ERR_FILE_READ",
        message: "Error reading file",
      });
    };

    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    setAccountNumbers([]);
    setFileName("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Sample template — list of example account numbers, one per line.
  const handleDownload = () => {
    const sampleContent = ["320003130343", "320003130344", "320003130345"].join("\n");
    const blob = new Blob([sampleContent], { type: "text/plain;charset=utf-8;" });
    saveAs(blob, "BaseAndHeatFactor_SampleTemplate.txt");
  };

  const handleSearchSubmit = () => {
    dispatch(alertActions.clear());
    if (uploadedFile && accountNumbers.length > 0) {
      onSearchSubmit(accountNumbers);
      handleDeleteFile();
      setIsOpen(false);
    } else if (accountNumber.trim()) {
      onSearchSubmit([accountNumber.trim()]);
      handleDeleteFile();
      setIsOpen(false);
    } else if (uploadedFile && accountNumbers.length === 0) {
      handleError({
        code: "ERR_MSG_085",
        message: "Account numbers not found",
      });
    }
  };

  const handleCancelClick = () => {
    setAccountNumber("");
    handleDeleteFile();
    setIsOpen(false);
    onClear?.();
  };

  const isSearchEnabled =
    accountNumber.trim().length > 0 ||
    (uploadedFile && accountNumbers.length > 0);

  return (
    <>
      <Button
        className="Filter"
        type="button"
        variant="contained"
        color="primary"
        aria-describedby={id}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setIsOpen(!isOpen);
        }}
      >
        <FilterListIcon />
        Filter
      </Button>

      <Popper
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        className="Filtercontainer marketerGroup Border"
      >
        <Box
          sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
          className="Filtercontainerinner"
        >
          <form
            className="Registrationcontainer marketerFiltercontainer"
            onSubmit={(e) => {
              e.preventDefault();
              if (isSearchEnabled) {
                handleSearchSubmit();
              }
            }}
          >
            <TextField
              variant="outlined"
              fullWidth
              className="SearchIconinput"
              margin="normal"
              placeholder="Search Account Number"
              value={accountNumber}
              onChange={(e) => {
                setAccountNumber(e.target.value);
              }}
              disabled={uploadedFile && accountNumbers.length > 0}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      className="SearchIcon"
                      onClick={() => {
                        if (isSearchEnabled) {
                          handleSearchSubmit();
                        }
                      }}
                      style={{
                        cursor: isSearchEnabled ? "pointer" : "not-allowed",
                        opacity: isSearchEnabled ? 1 : 0.5,
                      }}
                    />
                  </InputAdornment>
                ),
                "aria-label": "search account number",
              }}
            />

            <Typography component="div" className="userprofilelist">
              <Grid container direction="row" spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="h2" className="userprofilelistcontent">
                    MASS SEARCH
                  </Typography>
                </Grid>
              </Grid>
            </Typography>

            <Typography
              component="div"
              sx={{ flexDirection: "column" }}
              className="UploadContainer"
            >
              <Grid container spacing={3}>
                {/* Upload Button */}
                <Grid item xs={12}>
                  <Button
                    component="label"
                    variant="contained"
                    className="Uploadfiles"
                    startIcon={<img src={materialsymbolsupload} alt="Upload" />}
                  >
                    <span>Upload your files here</span>
                    <input
                      type="file"
                      hidden
                      accept=".txt"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                    />
                  </Button>
                </Grid>
                {uploadedFile && accountNumbers.length > 0 && (
                  <Grid item xs={12}>
                    <Typography component="div" className="SupportedFormats">
                      <Typography component="h3">Uploaded File</Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1,
                          mt: 1,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
                        >
                          {fileName}
                        </Typography>
                        <Box>
                          <IconButton size="small" onClick={handleDeleteFile}>
                            <img
                              src={Delete}
                              alt="Delete"
                              style={{ width: 20, height: 20 }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography component="div" className="SupportedFormats">
                    <Typography component="h3">Supported Formats</Typography>
                    <Typography component="div" className="fileformatlist">
                      {supportedFormats.map((format) => (
                        <span key={format}>{format} </span>
                      ))}
                    </Typography>
                  </Typography>
                  <Typography component="div" className="SupportedFormats">
                    <Typography component="h3">Download Template</Typography>
                    <Typography component="div" className="fileformatlist">
                      Sample File
                      <IconButton onClick={handleDownload}>
                        <img
                          src={materialsymbolsdownload}
                          alt="material-symbols_download"
                        />
                      </IconButton>
                    </Typography>
                  </Typography>
                </Grid>
              </Grid>
            </Typography>
            <Box className="CreateMarketerbutton" mt={2}>
              <Button
                type="submit"
                variant="contained"
                className="submitbutton"
                color="primary"
                disabled={!isSearchEnabled}
              >
                Search
              </Button>
              <Button
                variant="contained"
                className="cancelbutton"
                color="primary"
                style={{ marginLeft: "8px" }}
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Box>
      </Popper>
    </>
  );
};

export default BaseAndHeatFactorReportFilter;